import React, { createContext, FC, ReactNode, useEffect, useRef, useState } from 'react';

import { useSessionContext } from '~/contexts/SessionContext';

import { mainSidebar, SideBarContent, SideBarSection } from '../views/layouts/SideBar';

export interface SidebarContextProps {
  sidebarContent: SideBarContent;
  isSidebarNameChanged: boolean;
  setSidebarContent: (content: SideBarContent) => void;
}

interface SidebarProviderProps {
  children: ReactNode;
}

const SidebarContext = createContext<SidebarContextProps>({
  sidebarContent: undefined,
  isSidebarNameChanged: false,
  setSidebarContent: undefined,
});

const isDetailsPagePath = (pathname: string) =>
  /^\/app\/(organizations|audiences)\/[^/]+(?:\/(?!create-segment)|$)/.test(pathname);

export const SidebarProvider: FC<SidebarProviderProps> = ({ children }) => {
  const { orgMenuItemTypes, hasAudienceOnlyAccess, hasInviteToAnOrganization, hasAccessToAudience, loading } =
    useSessionContext();
  const [sidebarContent, setContentLocal] = useState<SideBarContent>(() =>
    isDetailsPagePath(window.location.pathname)
      ? { name: 'detail', sections: [{ items: [] }] }
      : mainSidebar(orgMenuItemTypes, hasAudienceOnlyAccess, hasInviteToAnOrganization, hasAccessToAudience, loading),
  );

  // prevNameRef always reflects the name of the last committed content — updated
  // synchronously inside setContent so there is never a stale-closure window.
  const prevNameRef = useRef(sidebarContent.name);
  const [isSidebarNameChanged, setIsSidebarNameChanged] = useState(false);

  // Reset the flag after one render so the CSS animation fires only once.
  useEffect(() => {
    if (isSidebarNameChanged) {
      setIsSidebarNameChanged(false);
    }
  }, [isSidebarNameChanged]);

  const isEqualSections = (sections1: SideBarSection[], sections2: SideBarSection[]) => {
    const stringifySections = (sectionsArray: SideBarSection[]) =>
      JSON.stringify(sectionsArray, (key, value) => (key !== 'icon' ? value : undefined));
    return stringifySections(sections1) === stringifySections(sections2);
  };

  const setContent = (contentNew: SideBarContent) => {
    if (!isEqualSections(sidebarContent?.sections, contentNew?.sections)) {
      const nameChanged = contentNew.name !== prevNameRef.current;
      prevNameRef.current = contentNew.name;
      setIsSidebarNameChanged(nameChanged);
      setContentLocal(contentNew);
    }
  };

  return (
    <SidebarContext.Provider
      value={{
        sidebarContent,
        isSidebarNameChanged,
        setSidebarContent: setContent,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebarContext: () => SidebarContextProps = () => {
  return React.useContext(SidebarContext);
};
